import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../../services/apiService';
import CongelamentoPanel from './CongelamentoPanel';
import '../MetasConfig.css';
import './Escalas.css';
import {
    DIAS_CURTOS, FUSOS, MAX_JANELA_DIAS, SOURCE_LABEL, addDias, brDate, celulaDia, classeCongelamento,
    classeDia, diaDaSemana, diaFechado, erroJanela, exigirForma, hojeISO, marcasDia, mensagemErro, primeiroDoMes,
    tituloDia, ultimoDoMes,
} from './escalasUtil';

const LEGENDA = [
    { cls: 'esc-dia-work', txt: 'Trabalha (escala)' },
    { cls: 'esc-dia-off', txt: 'Folga (escala)' },
    { cls: 'esc-dia-feriado-trabalha', txt: 'Feriado, escala trabalha' },
    { cls: 'esc-dia-nac', txt: 'Feriado nacional' },
    { cls: 'esc-dia-loja-off', txt: 'Loja: folga/fechada' },
    { cls: 'esc-dia-loja-work', txt: 'Loja: horário especial' },
    { cls: 'esc-dia-vend-off', txt: 'Vendedor: folga/falta/férias' },
    { cls: 'esc-dia-vend-work', txt: 'Vendedor: reposição/extra' },
    { cls: 'esc-dia-none', txt: 'Sem escala' },
];

// Marcas do congelamento do passado (dia anterior ao primeiro dia aberto). Passe o mouse no dia para o detalhe.
const LEGENDA_CONG = [
    { cls: 'esc-leg-alt', marca: 'r2', txt: 'Alterado depois de fechado (revisão maior que 1): o motivo está no detalhe' },
    { cls: 'esc-leg-div', marca: '!', txt: 'Divergente: o recálculo ao vivo difere do congelado' },
    { cls: 'esc-leg-nao', marca: '', txt: 'Histórico não congelado (calculado agora)' },
    { cls: 'esc-leg-pend', marca: '', txt: 'Fechado, congelando' },
];

/** Selos de uma célula do calendário: revisão (> 1) e divergência. */
function SelosCelula({ d }) {
    const m = marcasDia(d);
    if (!m.alterado && !m.divergente) return null;
    return (
        <>
            {m.alterado && <sup className="esc-selo esc-selo-alt" aria-label={`alterado na revisão ${m.revisao != null ? m.revisao : ''}`}>r{m.revisao != null ? m.revisao : '?'}</sup>}
            {m.divergente && <sup className="esc-selo esc-selo-div" aria-label="divergente">!</sup>}
        </>
    );
}

/**
 * Aba Calendário: a escala RESOLVIDA, dia a dia, para uma loja inteira ou um vendedor. É a tela que
 * responde "por que o fulano estava de folga dia 12?": cada dia é colorido pela origem e o motivo
 * (`why`) aparece ao passar o mouse. Também guarda o fuso e a região da loja (a região escolhe os
 * feriados regionais). Janela máxima de 62 dias por consulta, como a API.
 */
function CalendarioTab({ subdomain, lookups, notify }) {
    const { stores, salesmen, nomeLoja } = lookups;
    const hoje = hojeISO();

    const [modo, setModo] = useState('store');
    const [lojaId, setLojaId] = useState('');
    const [vendId, setVendId] = useState('');
    const [de, setDe] = useState(primeiroDoMes(hoje));
    const [ate, setAte] = useState(ultimoDoMes(hoje));
    const [resultado, setResultado] = useState(null);   // { modo, salesmen, de, ate } ou, em 'company', { modo, lojas: [{ id, salesmen, erro }], de, ate }
    const [abertas, setAbertas] = useState(() => new Set());   // lojas expandidas no modo empresa
    const [consultando, setConsultando] = useState(false);

    const [ajustes, setAjustes] = useState({});          // store_id -> {timezone, region, valid_from} vigente hoje
    const [historico, setHistorico] = useState([]);       // todas as linhas de fuso/região (com valid_from), de todas as lojas
    const [primeiroAberto, setPrimeiroAberto] = useState(null);   // first_open_day da API: dia anterior a ele é fechado
    const [ajustesCarregados, setAjustesCarregados] = useState(false);   // evita o painel piscar aberto antes de saber se há registro
    const [ajusteAberto, setAjusteAberto] = useState(false);
    const [formAjuste, setFormAjuste] = useState({ timezone: 'America/Sao_Paulo', region: '', valid_from: hoje });
    const [salvandoAjuste, setSalvandoAjuste] = useState(false);

    const erroPeriodo = erroJanela(de, ate);
    const lojaDoContexto = modo === 'company' ? '' : modo === 'store'
        ? lojaId
        : String(salesmen.find((s) => String(s.salesman_id) === String(vendId))?.store_id || '');
    const alvoOk = modo === 'company' ? stores.length > 0 : modo === 'store' ? !!lojaId : !!vendId;

    useEffect(() => { setResultado(null); setLojaId(''); setVendId(''); }, [subdomain]);

    const carregarAjustes = useCallback(async () => {
        if (!subdomain) return;
        try {
            const r = exigirForma(await apiService.scheduleListStoreSettings({ subdomain }), 'settings');
            setAjustes(Object.fromEntries(r.settings.map((s) => [String(s.store_id), s])));
            // API antiga não devolve `history`: o histórico de cada loja é só a linha vigente.
            setHistorico(Array.isArray(r.history) ? r.history : r.settings);
            if (r.first_open_day) setPrimeiroAberto(r.first_open_day);
            setAjustesCarregados(true);
        } catch (e) {
            notify(mensagemErro(e, 'Erro ao carregar o fuso das lojas'), 'error');
        }
    }, [subdomain, notify]);

    useEffect(() => { setAjustes({}); setHistorico([]); setAjustesCarregados(false); carregarAjustes(); }, [carregarAjustes]);

    // Loja com fuso/região já cadastrados: painel recolhido. Sem registro: aberto, pedindo o cadastro.
    useEffect(() => {
        if (ajustesCarregados && lojaDoContexto) setAjusteAberto(!ajustes[String(lojaDoContexto)]);
    }, [lojaDoContexto, ajustesCarregados, ajustes]);

    // A mudança de fuso e região só vale a partir do primeiro dia aberto: é a data que o formulário propõe.
    useEffect(() => {
        const a = ajustes[String(lojaDoContexto)];
        setFormAjuste({ timezone: a?.timezone || 'America/Sao_Paulo', region: a?.region || '', valid_from: primeiroAberto || hoje });
    }, [lojaDoContexto, ajustes, primeiroAberto, hoje]);

    const erroAjuste = !formAjuste.timezone.trim()
        ? 'Informe o fuso'
        : !formAjuste.valid_from
            ? 'Informe a partir de quando vale'
            : diaFechado(formAjuste.valid_from, primeiroAberto)
                ? `Só vale a partir de ${brDate(primeiroAberto)}: dias anteriores já estão fechados e o passado não muda`
                : null;

    // Linhas de fuso/região da loja em foco, da mais antiga para a mais nova; marca a que vale hoje.
    const historicoDaLoja = useMemo(() => {
        const doAlvo = historico.filter((h) => String(h.store_id) === String(lojaDoContexto))
            .sort((a, b) => String(a.valid_from || '').localeCompare(String(b.valid_from || '')));
        const ref = primeiroAberto || hoje;
        let vigente = null;
        doAlvo.forEach((h) => { if (!h.valid_from || String(h.valid_from).split('T')[0] <= ref) vigente = h; });
        return doAlvo.map((h) => ({ ...h, vigente: h === vigente, closed: h.closed != null ? !!h.closed : (!h.valid_from || diaFechado(h.valid_from, primeiroAberto)) }));
    }, [historico, lojaDoContexto, primeiroAberto, hoje]);

    const consultar = async () => {
        if (erroPeriodo || !alvoOk) return;
        setConsultando(true);
        setResultado(null);
        try {
            if (modo === 'company') {
                // Uma consulta por loja (a API agrupa por loja e limita a 300 vendedores por chamada), 4 por vez.
                const lojas = stores.map((s) => ({ id: s.id, nome: s.display_name || s.name, salesmen: [], erro: '' }));
                let proxima = 0;
                let primeiroDaConsulta = null;
                const trabalhador = async () => {
                    while (proxima < lojas.length) {
                        const l = lojas[proxima++];
                        try {
                            const r = await apiService.scheduleResolve({ subdomain, date_from: de, date_to: ate, store_id: Number(l.id) });
                            if (!r || r.success !== true || !Array.isArray(r.salesmen)) throw new Error('resposta fora de forma');
                            l.salesmen = r.salesmen;
                            if (r.first_open_day) primeiroDaConsulta = r.first_open_day;
                        } catch (e) {
                            l.erro = mensagemErro(e, 'Erro ao consultar a loja');
                        }
                    }
                };
                await Promise.all(Array.from({ length: Math.min(4, lojas.length) }, trabalhador));
                if (primeiroDaConsulta) setPrimeiroAberto(primeiroDaConsulta);
                setResultado({ modo, lojas, de, ate, nota: '' });
                setAbertas(new Set(lojas.filter((l) => l.salesmen.length > 0).map((l) => String(l.id))));
                return;
            }
            const corpo = { subdomain, date_from: de, date_to: ate };
            if (modo === 'store') corpo.store_id = Number(lojaId); else corpo.salesman_id = Number(vendId);
            const r = await apiService.scheduleResolve(corpo);
            if (!r || r.success !== true || !Array.isArray(r.salesmen)) throw new Error('resposta fora de forma');
            if (r.first_open_day) setPrimeiroAberto(r.first_open_day);
            setResultado({ modo, salesmen: r.salesmen, de, ate, nota: r.note || '' });
        } catch (e) {
            notify(mensagemErro(e, 'Erro ao consultar a escala'), 'error');
        } finally {
            setConsultando(false);
        }
    };

    const salvarAjuste = async () => {
        if (!lojaDoContexto) return;
        if (erroAjuste) { notify(erroAjuste, 'error'); return; }
        setSalvandoAjuste(true);
        try {
            const r = await apiService.scheduleSaveStoreSettings({
                subdomain, store_id: Number(lojaDoContexto), timezone: formAjuste.timezone, region: formAjuste.region.trim() || null,
                valid_from: formAjuste.valid_from,
            });
            if (!r || r.success !== true) throw new Error('resposta fora de forma');
            notify(`Fuso e região salvos: valem a partir de ${brDate(r.valid_from || formAjuste.valid_from)}`, 'success');
            setAjusteAberto(false);
            carregarAjustes();
        } catch (e) {
            notify(mensagemErro(e, 'Erro ao salvar o fuso da loja'), 'error');
        } finally {
            setSalvandoAjuste(false);
        }
    };

    const dias = useMemo(() => {
        if (!resultado) return [];
        const out = [];
        for (let d = resultado.de; d <= resultado.ate; d = addDias(d, 1)) out.push(d);
        return out;
    }, [resultado]);

    const atalho = (qual) => {
        if (qual === 'mes') { setDe(primeiroDoMes(hoje)); setAte(ultimoDoMes(hoje)); }
        if (qual === 'prox30') { setDe(hoje); setAte(addDias(hoje, 29)); }
        if (qual === 'prox60') { setDe(hoje); setAte(addDias(hoje, MAX_JANELA_DIAS - 1)); }
    };

    // Vendedor único: calendário em semanas (segunda a domingo). Loja: uma linha por vendedor.
    const renderSemanas = (s) => {
        const porData = Object.fromEntries(s.days.map((d) => [d.date, d]));
        const inicio = addDias(resultado.de, -diaDaSemana(resultado.de));
        const semanas = [];
        for (let ini = inicio; ini <= resultado.ate; ini = addDias(ini, 7)) {
            semanas.push(Array.from({ length: 7 }, (_, i) => addDias(ini, i)));
        }
        return (
            <div className="esc-semanas" role="grid" aria-label="Calendário do vendedor">
                {DIAS_CURTOS.map((n) => <div key={n} className="esc-semana-cab" role="columnheader">{n}</div>)}
                {semanas.map((sem) => sem.map((dia) => {
                    const d = porData[dia];
                    if (!d) return <div key={dia} className="esc-semana-vazio" />;
                    return (
                        <div key={dia} className={`esc-semana-dia ${classeDia(d)} ${classeCongelamento(d)}`} title={tituloDia(d)} role="gridcell">
                            <span className="esc-semana-num">{dia.slice(8)}</span>
                            <strong>{d.label}</strong>
                            {d.start && <span>{d.start}–{d.end}{d.ends_next_day ? ' +1' : ''}</span>}
                            {d.holiday && <small>{d.holiday_label || 'Feriado'}</small>}
                            {d.status === 'planned' && <small>planejada</small>}
                            {marcasDia(d).alterado && (
                                <small className="esc-selo-linha esc-selo-linha-alt">alterado · rev {marcasDia(d).revisao != null ? marcasDia(d).revisao : '?'}</small>
                            )}
                            {marcasDia(d).divergente && <small className="esc-selo-linha esc-selo-linha-div">divergente do congelado</small>}
                            {marcasDia(d).estado === 'nao_congelado' && <small className="esc-selo-linha">histórico não congelado</small>}
                            {marcasDia(d).estado === 'pendente' && <small className="esc-selo-linha">congelando</small>}
                        </div>
                    );
                }))}
            </div>
        );
    };

    const renderGrade = (salesmen) => (
        <div className="esc-tabela-rolagem">
            <table className="esc-cal">
                <thead>
                    <tr>
                        <th className="esc-cal-nome">Vendedor</th>
                        {dias.map((d) => (
                            <th key={d} className={diaDaSemana(d) >= 5 ? 'esc-cal-fds' : ''} title={brDate(d)}>
                                {d.slice(8)}<small>{DIAS_CURTOS[diaDaSemana(d)]}</small>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {salesmen.map((s) => {
                        const porData = Object.fromEntries(s.days.map((d) => [d.date, d]));
                        return (
                            <tr key={s.salesman_id}>
                                <th className="esc-cal-nome" scope="row">{s.name || s.username || `Vendedor ${s.salesman_id}`}</th>
                                {dias.map((dia) => {
                                    const d = porData[dia];
                                    return (
                                        <td key={dia} className={`esc-cal-cel ${classeDia(d)} ${classeCongelamento(d)}`} title={tituloDia(d)}>
                                            {celulaDia(d)}<SelosCelula d={d} />
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    const alternarLoja = (id) => setAbertas((prev) => {
        const novo = new Set(prev);
        if (novo.has(id)) novo.delete(id); else novo.add(id);
        return novo;
    });

    // Modo empresa: a mesma grade da loja, uma seção por loja, com expandir/esconder.
    const renderEmpresa = () => {
        const lojas = resultado.lojas;
        const comVendedor = lojas.filter((l) => l.salesmen.length > 0);
        const total = comVendedor.reduce((n, l) => n + l.salesmen.length, 0);
        return (
            <div className="esc-empresa-lista">
                <div className="esc-empresa-barra">
                    <span>{comVendedor.length} de {lojas.length} lojas com vendedores ativos · {total} vendedores</span>
                    <span>
                        <button className="mc-btn mc-btn-sm" onClick={() => setAbertas(new Set(comVendedor.map((l) => String(l.id))))}>Expandir todas</button>
                        <button className="mc-btn mc-btn-sm" onClick={() => setAbertas(new Set())}>Esconder todas</button>
                    </span>
                </div>
                {lojas.map((l) => {
                    const id = String(l.id);
                    const aberta = abertas.has(id);
                    const vazia = l.salesmen.length === 0;
                    return (
                        <section key={id} className="esc-loja-secao">
                            <button
                                className="esc-loja-cab"
                                onClick={() => alternarLoja(id)}
                                aria-expanded={aberta}
                                aria-controls={`esc-loja-${id}`}
                                disabled={vazia}
                            >
                                <span className="esc-loja-seta" aria-hidden="true">{aberta ? '▾' : '▸'}</span>
                                <strong>{l.nome}</strong>
                                <small>
                                    {l.erro ? l.erro : vazia ? 'sem vendedores ativos' : `${l.salesmen.length} vendedor${l.salesmen.length > 1 ? 'es' : ''}`}
                                </small>
                            </button>
                            {aberta && !vazia && <div id={`esc-loja-${id}`}>{renderGrade(l.salesmen)}</div>}
                        </section>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="mc-page">
            <p className="mc-nota">
                A escala <strong>resolvida</strong>, dia a dia. Passe o mouse num dia para ver o motivo e a origem
                ({Object.values(SOURCE_LABEL).join(', ').toLowerCase()}). Máximo de {MAX_JANELA_DIAS} dias por consulta.
                {primeiroAberto && (
                    <> Os dias anteriores a <strong>{brDate(primeiroAberto)}</strong> estão <strong>fechados</strong>: o que
                    valeu neles só muda por revisão, com motivo.</>
                )}
            </p>

            <div className="mc-filters">
                <div className="mc-field">
                    <label htmlFor="esc-cal-modo">Ver por</label>
                    <select id="esc-cal-modo" value={modo} onChange={(e) => { setModo(e.target.value); setResultado(null); }}>
                        <option value="company">Empresa (todas as lojas)</option>
                        <option value="store">Loja (todos os vendedores ativos)</option>
                        <option value="salesman">Vendedor</option>
                    </select>
                </div>
                {modo === 'company' ? null : modo === 'store' ? (
                    <div className="mc-field">
                        <label htmlFor="esc-cal-loja">Loja *</label>
                        <select id="esc-cal-loja" value={lojaId} onChange={(e) => setLojaId(e.target.value)}>
                            <option value="">— Selecione —</option>
                            {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                        </select>
                    </div>
                ) : (
                    <div className="mc-field">
                        <label htmlFor="esc-cal-vend">Vendedor *</label>
                        <select id="esc-cal-vend" value={vendId} onChange={(e) => setVendId(e.target.value)}>
                            <option value="">— Selecione —</option>
                            {salesmen.map((s) => (
                                <option key={s.salesman_id} value={s.salesman_id}>{s.salesman_name} — {s.store_name}{s.is_active ? '' : ' (inativo)'}</option>
                            ))}
                        </select>
                    </div>
                )}
                <div className="mc-field">
                    <label htmlFor="esc-cal-de">De</label>
                    <input id="esc-cal-de" type="date" value={de} onChange={(e) => setDe(e.target.value)} />
                </div>
                <div className="mc-field">
                    <label htmlFor="esc-cal-ate">Até</label>
                    <input id="esc-cal-ate" type="date" value={ate} onChange={(e) => setAte(e.target.value)} />
                </div>
                <div className="mc-field mc-filters-actions esc-acoes">
                    <button className="mc-btn mc-btn-sm" onClick={() => atalho('mes')}>Este mês</button>
                    <button className="mc-btn mc-btn-sm" onClick={() => atalho('prox30')}>30 dias</button>
                    <button className="mc-btn mc-btn-sm" onClick={() => atalho('prox60')}>62 dias</button>
                    <button className="mc-btn mc-btn-primary" onClick={consultar} disabled={consultando || !!erroPeriodo || !alvoOk || !subdomain}>
                        {consultando ? 'Consultando…' : 'Consultar'}
                    </button>
                </div>
            </div>
            {erroPeriodo && <p className="mc-nota">{erroPeriodo}</p>}

            {lojaDoContexto && ajustesCarregados && (() => {
                const registro = ajustes[String(lojaDoContexto)];
                return (
                    <div className={`mc-form-panel esc-ajuste${registro ? ' esc-ajuste-mini' : ''}`}>
                        <button
                            type="button"
                            className="esc-ajuste-cab"
                            onClick={() => setAjusteAberto((v) => !v)}
                            aria-expanded={ajusteAberto}
                            aria-controls="esc-ajuste-corpo"
                        >
                            <span aria-hidden="true">{ajusteAberto ? '▾' : '▸'}</span>
                            <strong>Fuso e região da loja: {nomeLoja(lojaDoContexto)}</strong>
                            <small>
                                {registro
                                    ? `${registro.timezone}${registro.region ? ` · ${registro.region}` : ''}`
                                    : 'não cadastrado — usando America/Sao_Paulo e só feriados sem região'}
                            </small>
                        </button>
                        {ajusteAberto && (
                            <div id="esc-ajuste-corpo" className="mc-form-grid">
                                <div className="mc-field">
                                    <label htmlFor="esc-fuso">Fuso (IANA)</label>
                                    <input id="esc-fuso" type="text" list="esc-fusos" value={formAjuste.timezone}
                                        onChange={(e) => setFormAjuste((p) => ({ ...p, timezone: e.target.value }))} />
                                    <datalist id="esc-fusos">{FUSOS.map((f) => <option key={f} value={f} />)}</datalist>
                                </div>
                                <div className="mc-field">
                                    <label htmlFor="esc-regiao">Região (UF ou cidade)</label>
                                    <input id="esc-regiao" type="text" maxLength={64} value={formAjuste.region}
                                        onChange={(e) => setFormAjuste((p) => ({ ...p, region: e.target.value }))} />
                                    <small className="esc-dica">Feriados nacionais com região só valem para lojas da mesma região.</small>
                                </div>
                                <div className="mc-field">
                                    <label htmlFor="esc-ajuste-de">Vale a partir de</label>
                                    <input id="esc-ajuste-de" type="date" value={formAjuste.valid_from} min={primeiroAberto || undefined}
                                        onChange={(e) => setFormAjuste((p) => ({ ...p, valid_from: e.target.value }))} />
                                    <small className="esc-dica">
                                        Só vale a partir da data escolhida{primeiroAberto ? ` (a mais cedo é ${brDate(primeiroAberto)})` : ''}; o passado não muda.
                                    </small>
                                </div>
                                <div className="mc-field mc-filters-actions esc-acoes">
                                    <button className="mc-btn mc-btn-primary" onClick={salvarAjuste} disabled={salvandoAjuste || !!erroAjuste}>
                                        {salvandoAjuste ? 'Salvando…' : 'Salvar'}
                                    </button>
                                </div>
                            </div>
                        )}
                        {ajusteAberto && erroAjuste && <p className="mc-nota">{erroAjuste}</p>}
                        {ajusteAberto && historicoDaLoja.length > 0 && (
                            <div className="esc-hist-bloco" aria-label="Histórico de fuso e região">
                                <h3>Histórico</h3>
                                <ul className="esc-hist" style={{ marginTop: 0 }}>
                                    {historicoDaLoja.map((h) => (
                                        <li key={`${h.store_id}-${h.valid_from}`} className={h.vigente ? 'esc-hist-vigente' : undefined}>
                                            <span>{h.valid_from ? `a partir de ${brDate(h.valid_from)}` : 'desde sempre'}</span>
                                            <span>{h.timezone}</span>
                                            <span>{h.region || 'sem região'}</span>
                                            {h.vigente && <span className="esc-selo-fechado">vigente</span>}
                                            {h.closed
                                                ? <span className="esc-selo-fechado" title="Já em vigor ou passado: não muda">histórico, não muda</span>
                                                : <span className="esc-selo-fechado esc-selo-fechado-aviso" title="Ainda não começou: pode ser ajustado">agendado</span>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                );
            })()}

            <div className="esc-legenda" aria-label="Legenda">
                {LEGENDA.map((l) => <span key={l.cls} className="esc-legenda-item"><i className={l.cls} />{l.txt}</span>)}
            </div>
            <div className="esc-legenda esc-legenda-cong" aria-label="Legenda do congelamento">
                {LEGENDA_CONG.map((l) => <span key={l.cls} className="esc-legenda-item"><i className={l.cls}>{l.marca}</i>{l.txt}</span>)}
            </div>

            {resultado && resultado.nota && <p className="mc-nota">{resultado.nota}</p>}
            {resultado && resultado.modo === 'company' && renderEmpresa()}
            {resultado && resultado.modo !== 'company' && resultado.salesmen.length === 0 && !resultado.nota && (
                <p className="mc-empty">Nenhum vendedor ativo para consultar.</p>
            )}
            {resultado && resultado.modo !== 'company' && resultado.salesmen.length > 0 && (
                resultado.modo === 'salesman' ? renderSemanas(resultado.salesmen[0]) : renderGrade(resultado.salesmen)
            )}

            {subdomain && <CongelamentoPanel subdomain={subdomain} notify={notify} />}
        </div>
    );
}

export default CalendarioTab;
